import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { saveReturnUrl } from '../utils/returnUrl';
import LogoIcon from '../components/common/icons/LogoIcon';

const PageContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #2B7A9B 0%, #8B3B8B 100%);
  position: relative;
  overflow: hidden;
`;

const Logo = styled.div`
  position: absolute;
  top: 20px;
  left: auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #00E5FF;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  margin-top: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

/**
 * BridgePage - Handles external access URLs (e.g., from Teams cards, emails)
 * 
 * Usage: /bridge?url=/rec/applicant/search/90253/profile?tab=message
 * 
 * This page:
 * 1. Checks if the user is authenticated
 * 2. If authenticated: redirects immediately to the target URL
 * 3. If not authenticated: saves the target URL and redirects to login
 * 4. Provides a centralized place for logging and access control
 */
const BridgePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const targetUrl = searchParams.get('url');

    // Log access attempt for monitoring and debugging
    console.log('[BridgePage] External access attempt:', {
      targetUrl,
      isAuthenticated,
      timestamp: new Date().toISOString(),
    });

    if (!targetUrl) {
      console.error('[BridgePage] Missing url parameter');
      navigate('/dashboard', { replace: true });
      return;
    }

    // Validate that the URL is safe (same-origin)
    if (!targetUrl.startsWith('/')) {
      console.error('[BridgePage] Invalid url parameter (must be relative):', targetUrl);
      navigate('/dashboard', { replace: true });
      return;
    }

    if (isAuthenticated) {
      // User is already logged in - redirect immediately to target
      console.log('[BridgePage] Authenticated user - redirecting to:', targetUrl);
      navigate(targetUrl, { replace: true });
    } else {
      // User is not logged in - save target and redirect to login
      console.log('[BridgePage] Unauthenticated user - saving returnUrl and redirecting to login');
      saveReturnUrl(targetUrl);
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  return (
    <PageContainer>
      <Logo>
        <LogoIcon />
      </Logo>
      <ContentContainer>
        <LoadingSpinner />
        <LoadingText>Redirecting...</LoadingText>
      </ContentContainer>
    </PageContainer>
  );
};

export default BridgePage;
